import {
  Alert,
  Box,
  Button,
  DatePicker,
  DateRangePicker,
  DateRangePickerProps,
  ExpandableSection,
  FormField,
  Input,
  Link,
  Popover,
  SegmentedControl,
  Select,
  SelectProps,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react';
import { EventInput, ShiftInput, ShiftTimingType } from '@amzn/red-velvet-api';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  buildDateTimeFromParts,
  toISODateString,
  toTimezoneTimeString,
} from '../../../../utils/dateFormatting';
import { OptionalLabel } from './Common';
import { useTimezone } from '../../../../hooks/timezone';
import { hasSameUtcOffset } from '../../../../utils/timezone';
import { formatMinutes } from '../../../shifts/timeModal';
import { FIELD_LIMITS, getCharLimitProps } from '../../../../utils/fieldConstraints';
import { featureIsEnabled, useFeatures } from '../../../../hooks/features';
import {
  COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_OFF,
  COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_ON,
  SHIFT_WAITLIST,
} from '../../../../utils/constants';
import { publishKatalMetric } from '../../../katalAnalytics';

const MS_PER_MINUTE = 60000;
const MAX_VOLUNTEERS_LIMIT = 2147483647; // Max signed 32-bit integer

const TIME_OPTIONS: SelectProps.Option[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const label = `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
  return { label, value };
});

export interface ShiftDetailsComponentProps {
  shift: ShiftInput;
  index: number;
  updateShift: (index: number, shift: ShiftInput) => void;
  onRemove?: (index: number) => void;
  errors?: Record<string, string>;
  expandOverride?: boolean;
  expandToggleKey?: number;
  isReadOnly?: boolean;
  eventFields: EventInput;
  onShiftTypeInfoClick?: () => void;
  metricPrefix: string;
  /** Opens the waitlist info help panel (info link next to the toggle). */
  onWaitlistInfoClick?: () => void;
  /** Edit flow: true while the event's active waitlist is still loading. Toggle is disabled. */
  waitlistInfoLoading?: boolean;
  /** Edit flow: true when the active-waitlist fetch failed. Toggle is disabled with a message. */
  waitlistInfoError?: boolean;
  /**
   * Create flow: every shift is being newly created (and auto-save may stamp a shiftId mid-build),
   * so an unset waitlistEnabled defaults ON once capped regardless of shiftId. Edit flow leaves this
   * false and keys the default on !shiftId (pre-existing -> OFF, newly-added -> ON). cce-4614/cce-7902.
   */
  isCreateFlow?: boolean;
  /** Edit flow: count of ACTIVE waitlist entries on this shift. */
  waitlistActiveCount?: number;
  /** Edit flow: invoked with the shiftId when the user turns the toggle off while active entries exist. */
  onAttemptDisableWaitlist?: (shiftId: string) => void;
}

export function ShiftDetailsComponent({
  shift,
  index,
  updateShift,
  onRemove,
  errors,
  expandOverride,
  expandToggleKey,
  isReadOnly,
  eventFields,
  onShiftTypeInfoClick,
  metricPrefix,
  onWaitlistInfoClick,
  waitlistInfoLoading,
  waitlistInfoError,
  waitlistActiveCount,
  onAttemptDisableWaitlist,
  isCreateFlow,
}: ShiftDetailsComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const waitlistFeatureEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  const userTimezone = useTimezone();
  const timeZoneForDates = eventFields.timeZone ? eventFields.timeZone : userTimezone;

  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (expandOverride !== undefined) {
      setExpanded(expandOverride);
    }
  }, [expandToggleKey, expandOverride]);

  const shiftType = shift.shiftTimingType ?? ShiftTimingType.SCHEDULED;
  const isFlexible = shiftType !== ShiftTimingType.SCHEDULED;
  const [name, setName] = useState(shift.name ?? '');
  const [date, setDate] = useState(toISODateString(shift.startDateTime, timeZoneForDates));
  const initStart = toTimezoneTimeString(shift.startDateTime, timeZoneForDates);
  const initEnd = toTimezoneTimeString(shift.endDateTime, timeZoneForDates);
  const timesUnset = isFlexible
    ? TIME_OPTIONS.find((o) => o.value === initEnd) === undefined
    : shift.shiftLength === 0;
  const [startTime, setStartTime] = useState(timesUnset ? '' : initStart);
  const [endTime, setEndTime] = useState(
    isFlexible && initEnd === '23:59' ? '' : timesUnset ? '' : initEnd
  );
  const [startDate, setStartDate] = useState(
    toISODateString(shift.startDateTime, timeZoneForDates)
  );
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = DateTime.fromJSDate(new Date(shift.startDateTime), { zone: timeZoneForDates })
      .plus({ days: 1 })
      .toISODate(); // e.g., "2024-03-16"
    return toISODateString(shift.endDateTime, timeZoneForDates) || tomorrow || '';
  });
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(() => {
    const sd = toISODateString(shift.startDateTime, timeZoneForDates);
    const ed = toISODateString(shift.endDateTime, timeZoneForDates);
    return sd && ed ? { type: 'absolute', startDate: sd, endDate: ed } : null;
  });
  const [defaultShiftLength, setDefaultShiftLength] = useState(() => {
    const mins = shift.shiftLength;
    return mins != null && mins > 0 ? formatMinutes(mins) : '';
  });
  const [maxVolunteers, setMaxVolunteers] = useState(
    shift.maxSignup != null && shift.maxSignup > 0 ? String(shift.maxSignup) : ''
  );
  const [endTimeError, setEndTimeError] = useState('');
  const [shiftLengthError, setShiftLengthError] = useState('');

  const shiftRef = useRef(shift);
  shiftRef.current = shift;

  const updateParent = (overrides: Partial<ShiftInput>) => {
    updateShift(index, { ...shiftRef.current, ...overrides });
  };

  // cce-7844: an unlimited-capacity shift (Maximum volunteers empty or 0) has no use for a
  // waitlist, so the toggle is disabled. Derived from the live maxVolunteers field state, so it
  // reacts as the coordinator types — clearing the field immediately disables the toggle.
  const isUnlimitedCapacity = maxVolunteers.trim() === '' || parseInt(maxVolunteers, 10) === 0;

  const hasActiveWaitlistEntries = !!waitlistActiveCount && waitlistActiveCount > 0;

  // --- Waitlist toggle: a waitlist only applies to a CAPPED shift ---
  //  capacity   entries  load-state  | toggle       tooltip  shows   auto-off write
  //  ---------  -------  ----------  | -----------  -------  ------  --------------
  //  capped     any      loaded      | interactive  no       intent  no
  //  unlimited  none     loaded      | disabled     yes      OFF     yes if was ON
  //  unlimited  some     loaded      | interactive  no       intent  no; use confirm
  //  unlimited  any      loading/err | disabled     no       intent  no
  //  intent = shift.waitlistEnabled ?? (isCreateFlow || !shiftId); in create every shift defaults
  //  capped->ON (auto-save may stamp a shiftId mid-build); in edit a pre-existing shift (has shiftId)
  //  with no stored value predates the flag -> OFF, a newly-added one (no shiftId) -> ON. cce-4614

  // Disable + tooltip only for an unlimited shift with no entries and a known count.
  // Loading/error are excluded so the tooltip does not clash with the FormField error.
  const disableForUnlimited =
    isUnlimitedCapacity && !hasActiveWaitlistEntries && !waitlistInfoLoading && !waitlistInfoError;

  // What the toggle shows: OFF when disabled, otherwise the persisted intent. When unset, the default
  // is keyed on whether the shift is new: the whole create flow (isCreateFlow) or, in edit, a shift
  // with no shiftId yet defaults ON once capped; a pre-existing edit-flow shift (has shiftId, predates
  // the flag) defaults OFF -- mirroring applyWaitlistEnabledDefault. Explicit true/false always win.
  const effectiveWaitlistEnabled = disableForUnlimited
    ? false
    : (shift.waitlistEnabled ?? (isCreateFlow || !shift.shiftId));

  // Auto-off only an explicitly enabled waitlist; never overwrite undefined or false, and never in
  // a read-only render (the toggle's disabled state already includes isReadOnly).
  useEffect(() => {
    if (
      !isReadOnly &&
      waitlistFeatureEnabled &&
      disableForUnlimited &&
      shift.waitlistEnabled === true
    ) {
      updateParent({ waitlistEnabled: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, waitlistFeatureEnabled, disableForUnlimited, shift.waitlistEnabled]);

  const setShiftType = (type: string) => {
    const now = new Date();
    if (type === 'flexible') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const sd = toISODateString(now, timeZoneForDates);
      const ed = toISODateString(tomorrow, timeZoneForDates);
      setStartDate(sd);
      setEndDate(ed);
      setStartTime('');
      setEndTime('');
      setDateRange({ type: 'absolute', startDate: sd, endDate: ed });
      setDefaultShiftLength('');
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: buildDateTimeFromParts(sd, '00:00', timeZoneForDates),
        endDateTime: buildDateTimeFromParts(ed, '23:59', timeZoneForDates),
        shiftLength: 0,
      });
    } else {
      const today = toISODateString(now, timeZoneForDates);
      setDate(today);
      setStartTime('');
      setEndTime('');
      const midnight = buildDateTimeFromParts(today, '00:00', timeZoneForDates);
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: midnight,
        endDateTime: midnight,
        shiftLength: 0,
      });
    }
  };

  const isScheduled = shiftType === ShiftTimingType.SCHEDULED;

  const formatTimeLabel = (time: string) =>
    TIME_OPTIONS.find((o) => o.value === time)?.label ?? time;

  const headerDescription = isScheduled
    ? `${date || t('shiftStep.noDateSet')} • ${startTime && endTime ? `${formatTimeLabel(startTime)} - ${formatTimeLabel(endTime)}` : t('shiftStep.noTimeSet')}`
    : `${startDate || t('shiftStep.noDateSet')} - ${endDate || t('shiftStep.noDateSet')}${startTime && endTime ? ` • ${formatTimeLabel(startTime)} - ${formatTimeLabel(endTime)}` : ''}`;

  const nameCharProps = getCharLimitProps(
    name,
    FIELD_LIMITS.SHIFT_NAME,
    t,
    'shiftStep.shiftNameConstraint'
  );

  return (
    <ExpandableSection
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerDescription={headerDescription}
      headerText={name || t('shiftStep.defaultShiftName')}
      variant="container"
      headerActions={
        onRemove && !isReadOnly ? (
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={t('shiftStep.removeShift')}
            data-aci-analytics-name={`${metricPrefix}_shiftRemoved`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          />
        ) : undefined
      }
    >
      <SpaceBetween size="l">
        <FormField
          description={t('shiftStep.shiftNameDescription')}
          label={<OptionalLabel>{t('shiftStep.shiftName')}</OptionalLabel>}
          constraintText={nameCharProps.constraintText}
          errorText={errors?.[`shift_${index}_name`] ?? nameCharProps.errorText}
        >
          <Input
            placeholder={t('shiftStep.defaultShiftName')}
            value={name}
            onChange={({ detail }) => {
              setName(detail.value);
            }}
            onBlur={() => updateParent({ name })}
            readOnly={isReadOnly}
          />
        </FormField>

        <hr data-volunteer-center-hr />

        <FormField
          label={t('shiftStep.shiftType')}
          info={
            onShiftTypeInfoClick && (
              <Link
                variant="info"
                data-aci-analytics-name="eventCreation_shiftTypeInfoOpened"
                onFollow={(e) => {
                  e.preventDefault();
                  onShiftTypeInfoClick();
                }}
              >
                {t('misc.info')}
              </Link>
            )
          }
        >
          <SegmentedControl
            options={[
              {
                iconName: 'calendar',
                id: 'scheduled',
                text: t('shiftStep.scheduledShift'),
                disabled: isReadOnly,
              },
              {
                iconName: 'calendar',
                id: 'flexible',
                text: t('shiftStep.flexibleShift'),
                disabled: isReadOnly,
              },
            ]}
            selectedId={shiftType}
            onChange={({ detail }) => setShiftType(detail.selectedId)}
          />
        </FormField>

        {isScheduled ? (
          <>
            <FormField
              description={t('shiftStep.shiftDateDescription')}
              label={t('shiftStep.shiftDate')}
              errorText={errors?.[`shift_${index}_date`]}
            >
              <DatePicker
                placeholder="YYYY/MM/DD"
                value={date}
                onChange={({ detail }) => setDate(detail.value)}
                onBlur={() => {
                  const start = buildDateTimeFromParts(date, startTime, timeZoneForDates);
                  const end = buildDateTimeFromParts(date, endTime, timeZoneForDates);
                  const raw = date
                    ? buildDateTimeFromParts(date, '00:00', timeZoneForDates)
                    : undefined;
                  const midnight = raw && !isNaN(raw.getTime()) ? raw : undefined;
                  updateParent({
                    startDateTime: start ?? midnight ?? shiftRef.current.startDateTime,
                    endDateTime: end ?? midnight ?? shiftRef.current.endDateTime,
                  });
                }}
                i18nStrings={{
                  previousMonthAriaLabel: t('shiftStep.previousMonthAriaLabel'),
                  nextMonthAriaLabel: t('shiftStep.nextMonthAriaLabel'),
                  todayAriaLabel: t('shiftStep.todayAriaLabel'),
                  openCalendarAriaLabel: (selectedDate) =>
                    selectedDate
                      ? t('shiftStep.chooseDateAriaLabel', { selectedDate })
                      : t('shiftStep.chooseDateDefaultAriaLabel'),
                }}
                readOnly={isReadOnly}
              />
            </FormField>
            <SpaceBetween direction="horizontal" size="l">
              <div style={{ minWidth: 240 }}>
                <FormField
                  description={eventFields.timeZone}
                  label={t('shiftStep.shiftStartTime')}
                  errorText={errors?.[`shift_${index}_startTime`]}
                >
                  <Select
                    selectedOption={TIME_OPTIONS.find((o) => o.value === startTime) ?? null}
                    options={TIME_OPTIONS}
                    placeholder={t('shiftStep.selectTimePlaceholder')}
                    filteringType="auto"
                    onChange={({ detail }) => {
                      const val = detail.selectedOption.value ?? '';
                      setStartTime(val);
                      if (endTime && endTime <= val) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const start = buildDateTimeFromParts(date, val, timeZoneForDates);
                      if (!start) return;
                      const end = endTime
                        ? buildDateTimeFromParts(date, endTime, timeZoneForDates)
                        : null;
                      const mins = end
                        ? Math.floor((end.getTime() - start.getTime()) / MS_PER_MINUTE)
                        : 0;
                      updateParent({ startDateTime: start, shiftLength: mins > 0 ? mins : 0 });
                    }}
                    readOnly={isReadOnly}
                  />
                </FormField>
              </div>
              <div style={{ minWidth: 240 }}>
                <FormField
                  description={eventFields.timeZone}
                  label={t('shiftStep.shiftEndTime')}
                  errorText={endTimeError || errors?.[`shift_${index}_endTime`]}
                >
                  <Select
                    selectedOption={TIME_OPTIONS.find((o) => o.value === endTime) ?? null}
                    options={TIME_OPTIONS}
                    placeholder={t('shiftStep.selectTimePlaceholder')}
                    filteringType="auto"
                    onChange={({ detail }) => {
                      const val = detail.selectedOption.value ?? '';
                      setEndTime(val);
                      if (startTime && val <= startTime) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const end = buildDateTimeFromParts(date, val, timeZoneForDates);
                      if (!end) return;
                      const start = startTime
                        ? buildDateTimeFromParts(date, startTime, timeZoneForDates)
                        : null;
                      const mins = start
                        ? Math.floor((end.getTime() - start.getTime()) / MS_PER_MINUTE)
                        : 0;
                      updateParent({ endDateTime: end, shiftLength: mins > 0 ? mins : 0 });
                    }}
                    readOnly={isReadOnly}
                  />
                </FormField>
              </div>
            </SpaceBetween>
            {eventFields.timeZone &&
            !hasSameUtcOffset(eventFields.timeZone, userTimezone, shift.startDateTime) ? (
              <Alert
                type="warning"
                i18nStrings={{
                  warningIconAriaLabel: t('shiftStep.timeZoneMismatchAriaLabel'),
                }}
              >
                {t('shiftStep.timeZoneMismatch', {
                  userTimezone: userTimezone,
                  eventTimezone: eventFields.timeZone,
                  interpolation: { escapeValue: false },
                })}
              </Alert>
            ) : null}
          </>
        ) : (
          <>
            <SpaceBetween direction="horizontal" size="l">
              <FormField
                label={t('shiftStep.shiftDateRange')}
                description={t('shiftStep.shiftDateRangeDescription')}
                errorText={
                  errors?.[`shift_${index}_startDate`] || errors?.[`shift_${index}_endDate`]
                }
              >
                <DateRangePicker
                  value={dateRange}
                  onChange={({ detail }) => {
                    setDateRange(detail.value);
                    if (detail.value?.type === 'absolute') {
                      const { startDate: sd, endDate: ed } = detail.value;
                      setStartDate(sd);
                      setEndDate(ed);
                      if (sd === ed && startTime && endTime && endTime <= startTime) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const updates: Partial<ShiftInput> = {};
                      const start = buildDateTimeFromParts(
                        sd,
                        startTime || '00:00',
                        timeZoneForDates
                      );
                      const end = buildDateTimeFromParts(ed, endTime || '23:59', timeZoneForDates);
                      if (start) updates.startDateTime = start;
                      if (end) updates.endDateTime = end;
                      updateParent(updates);
                    }
                  }}
                  relativeOptions={[]}
                  isValidRange={(range) => {
                    if (range?.type === 'absolute') {
                      if (!range.startDate || !range.endDate) {
                        return { valid: false, errorMessage: t('shiftStep.dateRangeIncomplete') };
                      }
                      if (range.startDate > range.endDate) {
                        return {
                          valid: false,
                          errorMessage: t('shiftStep.endDateMustBeAfterStartDate'),
                        };
                      }
                    }
                    return { valid: true };
                  }}
                  i18nStrings={
                    t('absoluteDatePicker', {
                      returnObjects: true,
                    }) as DateRangePickerProps.I18nStrings
                  }
                  placeholder="YYYY/MM/DD – YYYY/MM/DD"
                  dateOnly
                  rangeSelectorMode="absolute-only"
                  expandToViewport
                  readOnly={isReadOnly}
                />
              </FormField>
              <FormField
                label={t('shiftStep.defaultShiftLength')}
                description={t('shiftStep.defaultShiftLengthDescription')}
                errorText={shiftLengthError || errors?.[`shift_${index}_shiftLength`]}
              >
                <Input
                  placeholder="hh:mm"
                  inputMode="numeric"
                  value={defaultShiftLength}
                  onChange={({ detail }) => {
                    const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                    if ((rawTime.match(/:/g) || []).length > 1) return;
                    const parts = rawTime.split(':');
                    if (parts[0].length > 2 || (parts[1] !== undefined && parts[1].length > 2))
                      return;
                    setDefaultShiftLength(rawTime);
                    const h = parseInt(parts[0]) || 0;
                    const m = parts[1] !== undefined ? parseInt(parts[1]) || 0 : 0;
                    const err =
                      parts[1] !== undefined && m > 59
                        ? t('shiftStep.invalidMinutes')
                        : h * 60 + m > 720
                          ? t('shiftStep.defaultShiftLengthExceeded')
                          : '';
                    setShiftLengthError(err);
                  }}
                  onBlur={() => {
                    const parts = defaultShiftLength.split(':');
                    if (parts[1] !== undefined && parts[1].length !== 2) {
                      setShiftLengthError(t('shiftStep.invalidShiftLengthFormat'));
                      return;
                    }
                    const hours = parseInt(parts[0]) || 0;
                    const mins = parseInt(parts[1]) || 0;
                    if (mins > 59) {
                      setShiftLengthError(t('shiftStep.invalidMinutes'));
                      return;
                    }
                    const total = hours * 60 + mins;
                    if (total > 720) {
                      setShiftLengthError(t('shiftStep.defaultShiftLengthExceeded'));
                      return;
                    }
                    if (total <= 0) {
                      setShiftLengthError(t('error.shiftLengthRequired'));
                      return;
                    }
                    setShiftLengthError('');
                    updateParent({ shiftLength: total });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
            </SpaceBetween>
            <SpaceBetween direction="horizontal" size="l">
              <FormField
                description={eventFields.timeZone}
                label={<OptionalLabel>{t('shiftStep.flexibleStartTime')}</OptionalLabel>}
              >
                <Select
                  selectedOption={TIME_OPTIONS.find((o) => o.value === startTime) ?? null}
                  options={TIME_OPTIONS}
                  placeholder={t('shiftStep.selectTimePlaceholder')}
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const val = detail.selectedOption.value ?? '';
                    setStartTime(val);
                    if (startDate === endDate && endTime && endTime <= val) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                    const start = buildDateTimeFromParts(startDate, val, timeZoneForDates);
                    if (start) updateParent({ startDateTime: start });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
              <FormField
                description={eventFields.timeZone}
                label={<OptionalLabel>{t('shiftStep.flexibleEndTime')}</OptionalLabel>}
                errorText={endTimeError || errors?.[`shift_${index}_endTime`]}
              >
                <Select
                  selectedOption={TIME_OPTIONS.find((o) => o.value === endTime) ?? null}
                  options={TIME_OPTIONS}
                  placeholder={t('shiftStep.selectTimePlaceholder')}
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const val = detail.selectedOption.value ?? '';
                    setEndTime(val);
                    if (startDate === endDate && startTime && val <= startTime) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                    const end = buildDateTimeFromParts(endDate, val, timeZoneForDates);
                    if (end) updateParent({ endDateTime: end });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
            </SpaceBetween>
          </>
        )}

        <hr data-volunteer-center-hr />

        <FormField
          description={t('shiftStep.maxVolunteersDescription')}
          label={<OptionalLabel>{t('shiftStep.maxVolunteers')}</OptionalLabel>}
        >
          <Input
            placeholder={
              waitlistFeatureEnabled ? t('shiftStep.maxVolunteersUnlimitedPlaceholder') : '0'
            }
            inputMode="numeric"
            value={maxVolunteers}
            onChange={({ detail }) => {
              if (detail.value === '') {
                setMaxVolunteers('');
                return;
              }
              if (!/^\d+$/.test(detail.value)) return;
              const parsed = parseInt(detail.value);
              // cce-7857: behind ShiftWaitlist -- reject 0 (a blank field is the unlimited sentinel).
              // Flag off keeps the legacy "0" placeholder and allows 0.
              if (waitlistFeatureEnabled && parsed < 1) return;
              if (parsed > MAX_VOLUNTEERS_LIMIT) return;
              setMaxVolunteers(String(parsed));
            }}
            onBlur={() =>
              updateParent({ maxSignup: maxVolunteers === '' ? 0 : parseInt(maxVolunteers) })
            }
            readOnly={isReadOnly}
          />
        </FormField>

        {waitlistFeatureEnabled && (
          <FormField errorText={waitlistInfoError ? t('shiftStep.waitlistLoadError') : undefined}>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {(() => {
                const waitlistToggle = (
                  <Toggle
                    checked={effectiveWaitlistEnabled}
                    disabled={
                      isReadOnly || waitlistInfoLoading || waitlistInfoError || disableForUnlimited
                    }
                    onChange={({ detail }) => {
                      if (detail.checked) {
                        publishKatalMetric(COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_ON, '');
                        updateParent({ waitlistEnabled: true });
                      } else if (
                        shift.shiftId &&
                        waitlistActiveCount &&
                        waitlistActiveCount > 0 &&
                        onAttemptDisableWaitlist
                      ) {
                        // Metric deferred to handleConfirmDisableWaitlist — firing here would inflate
                        // the count when the user dismisses the confirmation modal without disabling.
                        onAttemptDisableWaitlist(shift.shiftId);
                      } else {
                        publishKatalMetric(COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_OFF, '');
                        updateParent({ waitlistEnabled: false });
                      }
                    }}
                  >
                    {effectiveWaitlistEnabled
                      ? t('shiftStep.waitlistEnabledLabel')
                      : t('shiftStep.waitlistDisabledLabel')}
                  </Toggle>
                );
                // cce-7844: only wrap in the explanatory Popover when the toggle is actually
                // disabled-for-unlimited (unlimited AND no active entries). If it still has
                // waitlisters the toggle stays interactive, so no tooltip and no wrap.
                return disableForUnlimited ? (
                  <Popover
                    size="small"
                    position="top"
                    triggerType="custom"
                    dismissButton={false}
                    content={t('shiftStep.waitlistUnlimitedTooltip')}
                  >
                    {waitlistToggle}
                  </Popover>
                ) : (
                  waitlistToggle
                );
              })()}
              {onWaitlistInfoClick && (
                <>
                  <Box color="text-status-inactive" display="inline">
                    |
                  </Box>
                  <Link
                    variant="info"
                    data-aci-analytics-name="eventCreation_waitlistInfoOpened"
                    onFollow={(e) => {
                      e.preventDefault();
                      onWaitlistInfoClick();
                    }}
                  >
                    {t('misc.info')}
                  </Link>
                </>
              )}
            </SpaceBetween>
          </FormField>
        )}

        <hr data-volunteer-center-hr />
      </SpaceBetween>
    </ExpandableSection>
  );
}
